import { Button } from "@/components/ui/baseUi/button"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/baseUi/dialog"
import { Separator } from "@/components/ui/baseUi/separator"
import { Text } from "@/components/ui/baseUi/text"
import { useCurrentBox } from "@/modules/box/hooks/useBox"
import { BoxShopSendEmail } from "@/modules/boxShop/components/boxShopSendEmail"
import { BoxShopSalesPayement } from "@/modules/boxShop/form/boxShopFormik"
import type { BoxShopSalesCouponDto } from "@/modules/boxShop/gateway/boxShopSalesApiDto"
import type { BoxShopOrderItem } from "@/modules/boxShop/type/boxShopType"
import { CouponType } from "@/modules/common/couponObject/gateway/couponObjectApiDto"
import { word } from "@/modules/core/utils/i18n"
import { formatCurrency } from "@/modules/currency/currency"
import { useLanguage } from "@/modules/language/hooks/useLanguage"
import { cn } from "@/modules/lib/utils"
import boxShopShoppingVector from "@images/illustrations/boxShopShoppingVector.svg"
import { QRCodeSVG } from "qrcode.react"
import { Fragment } from "react"

type BoxShopSalesDialogProps = {
  orderItems: BoxShopOrderItem[]
  coupon: BoxShopSalesCouponDto | null
  userId: string | null
  payment: BoxShopSalesPayement
  saleId: string
  checkoutUrl: string | undefined
  open: boolean
  onOpenChange: (open: boolean) => void
  handleResetOrder: () => void
} & React.ComponentProps<typeof DialogContent>

export function BoxShopSalesDialog({
  orderItems,
  coupon,
  userId,
  payment,
  saleId,
  checkoutUrl,
  open,
  onOpenChange,
  handleResetOrder,
  ...props
}: BoxShopSalesDialogProps) {
  const language = useLanguage()
  const { result: currentBox } = useCurrentBox()

  return (
    <Dialog
      open={open}
      onOpenChange={(openValue, eventDetails) => {
        // Keep the dialog open when the user clicks outside of it
        if (!openValue && eventDetails.reason === "outside-press") return
        onOpenChange(openValue)
      }}
      modal={true}
    >
      <DialogContent
        {...props}
        className={cn(
          "max-h-[calc(100vh-4rem)] overflow-y-auto rounded-xl bg-surface-background-float",
          payment === BoxShopSalesPayement.DIRECT ? "w-lg" : "w-4xl",
          props.className,
        )}
      >
        <DialogHeader>
          <DialogTitle>
            <Text fontStyle="HS" className="font-spaceMono!">
              {word("boxShop.salesDialog.resume.title")}
            </Text>
          </DialogTitle>
          <DialogDescription className="sr-only"></DialogDescription>
        </DialogHeader>

        <div
          className={cn(
            "flex px-1 py-6",
            payment === BoxShopSalesPayement.DIRECT ? "flex-col" : "flex-col gap-8 md:flex-row",
          )}
        >
          <div className="relative flex h-fit grow flex-col gap-4 bg-[linear-gradient(180deg,#1A1A1A_0%,#1F1F1F_50%,#121212_100%)] pt-4 pr-4 pb-18 pl-6">
            {/* NEW ORDER VECTOR */}
            <div
              aria-hidden="true"
              className="pointer-events-none absolute inset-0 z-0 w-full bg-auto bg-top bg-repeat-x"
              style={{ backgroundImage: `url(${boxShopShoppingVector})` }}
            />
            {/* NEW ORDER TITLE */}
            <Text fontStyle="HS" className="mt-7 font-spaceMono!">
              {word("boxShop.salesDialog.orderSummary.title").toUpperCase()}
            </Text>

            {/* NEW ORDER TABLE */}
            <div className="grid grid-cols-12 gap-3">
              <div className="col-span-6">
                <Text fontStyle="BM" className="font-spaceMono!">
                  {word("boxShop.shoppingView.newOrder.table.header.itemName")}
                </Text>
              </div>
              <div className="col-span-2 flex">
                <Text fontStyle="BM" className="w-full text-center font-spaceMono!">
                  {word("boxShop.shoppingView.newOrder.table.header.quantity")}
                </Text>
              </div>
              <div className="col-span-4 flex">
                <Text fontStyle="BM" className="w-full text-right font-spaceMono!">
                  {word("boxShop.shoppingView.newOrder.table.header.price")}
                </Text>
              </div>
              {orderItems.map((orderItem) => (
                <Fragment key={orderItem.product.id}>
                  <div key={orderItem.product.id + "-title"} className="col-span-6">
                    <Text fontStyle="BS" className="w-full text-center font-spaceMono! text-textOnSurface-muted">
                      {orderItem.product.title}
                    </Text>
                  </div>
                  <div
                    key={orderItem.product.id + "-quantity"}
                    className="col-span-2 flex flex-row items-center justify-between"
                  >
                    <Text fontStyle="BS" className="w-full text-center font-spaceMono! text-textOnSurface-muted">
                      {orderItem.quantity}
                    </Text>
                  </div>
                  <div key={orderItem.product.id + "-price"} className="col-span-4 flex justify-end">
                    <Text fontStyle="BS" className="w-full text-right font-spaceMono! text-textOnSurface-muted">
                      {formatCurrency(orderItem.product.price.value, orderItem.product.price.currency, language)}
                    </Text>
                  </div>
                </Fragment>
              ))}
            </div>

            {/* SUMMARY Discount, VAT & Total */}
            <div className="flex flex-col">
              {coupon && coupon.value !== 0 ? (
                <div className="flex w-full flex-row justify-between">
                  <Text fontStyle="BS" className="font-spaceMono! text-textOnSurface-muted">
                    {word("boxShop.shoppingView.newOrder.discount.label")}
                  </Text>
                  <Text fontStyle="BS" className="font-spaceMono! text-textOnSurface-muted">
                    {coupon.type === CouponType.PERCENT
                      ? `${String(coupon.value)}%`
                      : formatCurrency(-coupon.value, currentBox?.stripeAccountCurrency ?? "EUR", language)}
                  </Text>
                </div>
              ) : null}
              <div className="flex w-full flex-row justify-between">
                <Text fontStyle="BS" className="font-spaceMono! text-textOnSurface-muted">
                  {word("boxShop.shoppingView.newOrder.vat.label")}
                </Text>
                <Text fontStyle="BS" className="font-spaceMono! text-textOnSurface-muted">
                  {formatCurrency(
                    orderItems.reduce(
                      (acc, orderItem) => acc + (orderItem.product.taxAmount ?? 0) * orderItem.quantity,
                      0,
                    ),
                    currentBox?.stripeAccountCurrency ?? "EUR",
                    language,
                  )}
                </Text>
              </div>

              <div className="flex w-full flex-row justify-between">
                <div className="flex flex-row">
                  <Text fontStyle="TL" className="font-spaceMono!">
                    {word("boxShop.shoppingView.newOrder.total.label")}
                  </Text>
                  <Text fontStyle="LS">{word("boxShop.shoppingView.newOrder.total.tax")}</Text>
                </div>

                <Text fontStyle="TL" className="font-spaceMono!">
                  {formatCurrency(
                    Math.max(
                      0,
                      orderItems.reduce(
                        (acc, orderItem) => acc + orderItem.product.price.value * orderItem.quantity,
                        0,
                      ) -
                        (coupon
                          ? coupon.type === CouponType.PERCENT
                            ? orderItems.reduce((acc, item) => acc + item.product.price.value * item.quantity, 0) *
                              (coupon.value / 100)
                            : coupon.value
                          : 0),
                    ),
                    currentBox?.stripeAccountCurrency ?? "EUR",
                    language,
                  )}
                </Text>
              </div>
            </div>
          </div>
          {payment === BoxShopSalesPayement.DIRECT ? null : (
            <div className="flex grow flex-col gap-4">
              <Text fontStyle="TL" className="font-spaceMono!">
                {word("boxShop.salesDialog.sendingPayementOrder.title")}
              </Text>

              <BoxShopSendEmail saleId={saleId} userId={userId} />
              <div className="flex flex-row items-center justify-between gap-2">
                <Separator className="flex-1 bg-neutral-300" />
                <Text fontStyle="BS" className="font-spaceMono!">
                  {word("boxShop.salesDialog.sendingPayementOrder.or").toUpperCase()}
                </Text>
                <Separator className="flex-1 bg-neutral-300" />
              </div>

              <div className="flex flex-col gap-2">
                <Text fontStyle="TM" className="font-spaceMono!">
                  {word("boxShop.salesDialog.sendingPayementOrder.scanToPaid")}
                </Text>
                {checkoutUrl ? (
                  <div className="flex w-87.5 flex-row items-center justify-center rounded-2xl bg-[#121212] px-6 py-2">
                    <QRCodeSVG fgColor="#6450F7" bgColor="#121212" size={350} value={checkoutUrl} />
                  </div>
                ) : null}
              </div>
            </div>
          )}
        </div>
        <DialogFooter>
          <Button
            variant="secondarySecondary"
            size="lg"
            onClick={() => {
              onOpenChange(false)
              handleResetOrder()
            }}
          >
            {word("boxShop.salesDialog.sendingPayementOrder.finishAndExit")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
